import { CheerzApiProcessOutAPMTransaction } from "@Types/api/payment";
import { Currency } from "@Types/api/price";
import { PaymentMethods } from "@Types/cart";
import CreditCard from "@Types/creditCard";
import {
  ProcessOutConfiguration,
  ProcessOutError,
  ProcessOutSuccessPayment,
} from "@Types/processOut";
import { convertFromApiToCreditCardContent } from "@Utils/cart/creditCard";
import { errorMessages, generic } from "@Utils/cart/payment/errors";
import { getMetaContent } from "@Utils/meta";
import { goToOrder } from "@Utils/navigation";
import { getVariableByEnv } from "@Utils/variableByEnv";
import CheerzApiProvider from "./CheerzApiProvider";

const CartPaymentFormStyle = {
  "::placeholder": {
    color: "rgba(0, 0, 0, 0.87);",
  },
  fontSize: "15px",
};

export default class CheerzPaymentProvider {
  paymentForm: HTMLElement | null = null;
  // When ProcessOut will provide the client interface/type, we will be able to safely type it
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processOutClient: any;
  api: CheerzApiProvider;

  constructor() {
    const csrfToken = getMetaContent("csrf-token");
    if (!csrfToken) {
      throw new Error("CSRF Token not found");
    }
    this.api = new CheerzApiProvider("fr", csrfToken);
    const processOutApiKey = getVariableByEnv("REACT_APP_PROCESSOUT_API_KEY");

    if (processOutApiKey) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.processOutClient = new window.ProcessOut.ProcessOut(processOutApiKey);
    } else {
      throw new Error("Couldn't load payment service, API Key not present");
    }
  }

  loadProcessOutModal(): boolean {
    const formElement = document.getElementById("PaymentForm");
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.processOutClient.setupForm(
        formElement,
        { requireCVC: true, style: CartPaymentFormStyle },
        (form: HTMLElement) => {
          this.paymentForm = form;
        }
      );
    } catch {
      return false;
    }
    return true;
  }

  proceedToPayment(
    orderNumber: string,
    storeCreditCard: boolean,
    paymentMethod: string,
    paymentFailure: (err: string) => void,
    countryTag: string | undefined,
    currency: Currency,
    savedCreditCard?: CreditCard
  ): void {
    if (paymentMethod === "free") {
      this.processFreePayment(orderNumber, paymentFailure);
    } else if (paymentMethod === "cb" && !savedCreditCard) {
      this.processCbPayment(orderNumber, storeCreditCard, paymentFailure);
    } else if (paymentMethod === "cb" && savedCreditCard) {
      this.getProcessOutInvoice(orderNumber, storeCreditCard, paymentFailure);
    } else {
      paymentMethod =
        process.env.NODE_ENV === "production"
          ? this.convertPaymentMethodToGateway(paymentMethod, countryTag, currency)
          : "sandbox";
      void this.processAlternativePayment(
        orderNumber,
        paymentMethod,
        (res: ProcessOutSuccessPayment) => {
          this.checkPaymentStatus(res.pending, res.number, paymentFailure);
        },
        (err: string) => {
          paymentFailure(this.getTranslationKeyFromProcessOutError(err));
        }
      );
    }
  }

  checkPaymentStatus = (
    pending: boolean,
    orderNumber: string,
    paymentFailure: (err: string) => void
  ): void => {
    const maxRetry = 1000;
    let retryCount = 0;
    const retryDelay = 1000;

    const retry = () => {
      if (retryCount > maxRetry) {
        return Promise.reject();
      } else {
        retryCount++;
        return new Promise((resolve) => {
          return setTimeout(resolve, retryDelay);
        });
      }
    };

    const checkStatus = (): Promise<string> =>
      this.api.getPaymentStatus(orderNumber).then((data) => {
        if (!data.pending && data.success) {
          if (data.success) {
            return Promise.resolve(data.order_number);
          } else {
            return Promise.reject();
          }
        }
        return retry().then(() => checkStatus());
      });

    if (pending) {
      checkStatus()
        .then((checkedOrderNumber) =>
          goToOrder(checkedOrderNumber.substr(2, checkedOrderNumber.length))
        )
        .catch(() => paymentFailure(generic));
    } else {
      goToOrder(orderNumber.substr(2, orderNumber.length));
    }
  };

  protected processFreePayment = (
    orderNumber: string,
    paymentFailure: (message: string) => void
  ): void => {
    void this.api
      .createFreeTransaction({ order_number: orderNumber })
      .then((transaction) => {
        this.checkPaymentStatus(transaction.pending, transaction.order_number, paymentFailure);
      })
      .catch((err: ProcessOutError) =>
        paymentFailure(this.getTranslationKeyFromProcessOutError(err.code))
      );
  };

  getGatewayConfigurations = (
    countryTag: string | undefined,
    currency: Currency,
    callback: (prevState: PaymentMethods[]) => void
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.processOutClient.fetchGatewayConfigurations(
      {
        filter: "alternative-payment-methods",
      },
      (res: ProcessOutConfiguration[]) =>
        callback(this.formatAvailableGateways(res, countryTag, currency)),
      (err: ProcessOutError) => err
    );
  };

  getCustomerSavedCard = async (): Promise<CreditCard | undefined> => {
    const creditCardResponse = await this.api.getCreditCard();
    if (
      creditCardResponse?.identifier &&
      creditCardResponse.card_type &&
      creditCardResponse.month &&
      creditCardResponse.year
    ) {
      return convertFromApiToCreditCardContent(creditCardResponse);
    } else {
      return undefined;
    }
  };

  protected getAvailablePaymentMethods = (
    countryTag: string | undefined,
    currency: Currency
  ): {
    [key: string]: string;
  } => {
    const availablePaymentMethods: { [key: string]: string } = {
      paypalexpresscheckout: "paypal",
    };
    const bancontactCountries = ["BE"];
    const sofortCountries = ["DE", "AT"];

    if (countryTag && currency === "EUR" && bancontactCountries.includes(countryTag)) {
      availablePaymentMethods.stripebancontact = "bancontact";
    } else if (countryTag && currency === "EUR" && sofortCountries.includes(countryTag)) {
      availablePaymentMethods.stripesofort = "sofort";
    }

    return availablePaymentMethods;
  };

  protected formatAvailableGateways = (
    gateways: ProcessOutConfiguration[],
    countryTag: string | undefined,
    currency: Currency
  ): PaymentMethods[] => {
    const entries = Object.entries(this.getAvailablePaymentMethods(countryTag, currency));
    const gatewayList: PaymentMethods[] = [];
    gateways.map((gateway: ProcessOutConfiguration) => {
      for (const [key, value] of entries) {
        if (gateway.gateway.name === key) {
          //Can make it cleaner with availablePaymentMethods types in PaymentMethods, but it will be evolving
          gatewayList.push(value as PaymentMethods);
        }
      }
    });
    return gatewayList;
  };

  protected convertPaymentMethodToGateway = (
    paymentMethod: string,
    countryTag: string | undefined,
    currency: Currency
  ): string => {
    const entries = Object.entries(this.getAvailablePaymentMethods(countryTag, currency));
    for (const [key, value] of entries) {
      if (paymentMethod === value) {
        return key;
      }
    }
    return "";
  };

  protected payWithAlternativePayment = async (
    orderNumber: string,
    invoiceId: string,
    token: string,
    resolve: (res: CheerzApiProcessOutAPMTransaction) => void,
    reject: (err: string) => void
  ): Promise<void> => {
    return this.api
      .createTransaction({
        alternative_process_out_token: token,
        order_number: orderNumber,
        remote_invoice_id: invoiceId,
      })
      .then((transaction) =>
        resolve({
          number: transaction.order_number,
          pending: transaction.pending,
          success: transaction.success,
          transaction_id: transaction.id,
        })
      )
      .catch((error) => reject(error));
  };

  protected processCbPayment = (
    orderNumber: string,
    storeCreditCard: boolean,
    paymentFailure: (message: string) => void
  ): void => {
    const name = document.getElementById("cardholdername") as HTMLFormElement;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.processOutClient.tokenize(
      this.paymentForm,
      {
        name: name.value || "",
      },
      (token: string) =>
        this.getProcessOutInvoice(orderNumber, storeCreditCard, paymentFailure, token),
      (err: ProcessOutError) => {
        paymentFailure(this.getTranslationKeyFromProcessOutError(err.code));
      }
    );
  };

  protected getTranslationKeyFromProcessOutError = (code: string): string => {
    return errorMessages[code] ? errorMessages[code] : generic;
  };

  protected getProcessOutInvoice = (
    orderNumber: string,
    storeCreditCard: boolean,
    paymentFailure: (message: string) => void,
    token?: string
  ): void => {
    this.api
      .getProcessOutCard(orderNumber)
      .then((response) => {
        this.api
          .createProcessOutInvoice({
            order_number: orderNumber,
          })
          .then((invoice) =>
            this.makeProcessOutPayment(
              response.payment_token,
              invoice.remote_invoice_id,
              orderNumber,
              paymentFailure
            )
          )
          .catch((err: ProcessOutError) =>
            paymentFailure(this.getTranslationKeyFromProcessOutError(err.code))
          );
      })
      .catch(() => {
        this.api
          .createProcessOutInvoice({
            order_number: orderNumber,
          })
          .then(
            (invoice) =>
              token &&
              this.getProcessOutPaymentToken(
                orderNumber,
                token,
                invoice.remote_invoice_id,
                storeCreditCard,
                paymentFailure
              )
          )
          .catch((err: ProcessOutError) =>
            paymentFailure(this.getTranslationKeyFromProcessOutError(err.code))
          );
      });
  };

  protected getProcessOutPaymentToken = (
    orderNumber: string,
    token: string,
    invoiceId: string,
    storeCreditCard: boolean,
    paymentFailure: (message: string) => void
  ): void => {
    void this.api
      .saveProcessOutCard({ order_number: orderNumber, card_token: token, store: storeCreditCard })
      .then((paymentToken) =>
        this.makeProcessOutPayment(
          paymentToken.payment_token,
          invoiceId,
          orderNumber,
          paymentFailure
        )
      );
  };

  protected makeProcessOutPayment = (
    paymentToken: string,
    remoteInvoiceid: string,
    orderNumber: string,
    paymentFailure: (message: string) => void
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.processOutClient.makeCardPayment(
      remoteInvoiceid,
      paymentToken,
      { authorize_only: true },
      (invoiceId: string) =>
        this.processAuthorizedPayment(orderNumber, paymentToken, invoiceId, paymentFailure),
      (err: ProcessOutError) => {
        paymentFailure(this.getTranslationKeyFromProcessOutError(err.code));
      }
    );
  };

  protected processAuthorizedPayment = (
    orderNumber: string,
    paymentToken: string,
    invoiceId: string,
    paymentFailure: (message: string) => void
  ): void => {
    this.api
      .createTransaction({
        order_number: orderNumber,
        process_out_token: paymentToken,
        remote_invoice_id: invoiceId,
      })
      .then((transaction) => {
        this.checkPaymentStatus(transaction.pending, transaction.order_number, paymentFailure);
      })
      .catch((err: ProcessOutError) =>
        paymentFailure(this.getTranslationKeyFromProcessOutError(err.code))
      );
  };

  protected findGatewayConfiguration = (
    configurations: ProcessOutConfiguration[],
    gatewayName: string
  ): ProcessOutConfiguration => {
    const configuration = configurations.find((conf) => conf.gateway.name === gatewayName);
    // This is for handling the case when a gateway is not well-configured on ProcessOut
    if (configuration === undefined) {
      throw new Error("Gateway " + gatewayName + " is not well-configured");
    }
    return configuration;
  };

  protected loadGatewayConfiguration = (
    invoiceId: string,
    gatewayName: string
  ): Promise<ProcessOutConfiguration> => {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.processOutClient.fetchGatewayConfigurations(
        {
          filter: "alternative-payment-methods",
          invoiceID: invoiceId,
        },
        (configurations: ProcessOutConfiguration[]) =>
          resolve(this.findGatewayConfiguration(configurations, gatewayName)),
        (error: ProcessOutError) => reject(this.getTranslationKeyFromProcessOutError(error.code))
      );
    });
  };

  protected alternativePaymentModalPromise = (
    orderNumber: string,
    invoiceId: string,
    configuration: ProcessOutConfiguration,
    resolve: (res: ProcessOutSuccessPayment) => void,
    reject: (err: string) => void
  ): void => {
    const container = document.getElementById("alternative-process-out-container") as HTMLElement;
    const button = document.createElement("button");
    if (!container) {
      throw new Error();
    }
    container.appendChild(button);
    // case specific for bancontact which requires an issuer country, this is a hack.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    configuration.id =
      configuration.gateway_name === "checkoutcombcmc"
        ? configuration.id.concat("?additional_data[issuer_country]=BE")
        : configuration.id;
    configuration.hookForInvoice(
      button,
      (token: string) => {
        // here we have received the token from ProcessOut and are ready to capture it
        return this.payWithAlternativePayment(orderNumber, invoiceId, token, resolve, reject);
      },
      (error: ProcessOutError) => {
        // whitelisting this error code, so that it does not get ignored
        if (error.code === "customer.popup-blocked") {
          error.messages = [error.message];
        }
        return reject(error.message);
      }
    );
    button.click(); // this should open gateway modal
  };

  protected processAlternativePayment = async (
    orderNumber: string,
    gatewayName: string,
    resolve: (res: ProcessOutSuccessPayment) => void,
    reject: (err: string) => void
  ): Promise<void> => {
    return this.api
      .createProcessOutInvoice({ order_number: orderNumber })
      .then((invoice) => {
        if (invoice.already_paid) {
          resolve({ number: invoice.order_number, pending: false, success: true });
        } else {
          const invoiceId = invoice.remote_invoice_id;
          this.loadGatewayConfiguration(invoiceId, gatewayName)
            .then((configuration) =>
              this.alternativePaymentModalPromise(
                orderNumber,
                invoiceId,
                configuration,
                resolve,
                reject
              )
            )
            .catch((error: ProcessOutError) =>
              reject(this.getTranslationKeyFromProcessOutError(error.code))
            );
        }
      })
      .catch((error: ProcessOutError) =>
        reject(this.getTranslationKeyFromProcessOutError(error.code))
      );
  };
}
